import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import api from "../../api";
import styles from "./CompletionRateStats.module.css";

const COLORS = { completed: "#2e7d32", in_progress: "#f0a500", not_started: "#c62828" };

function StatCard({ label, value, rate, color }) {
  return (
    <div className={styles.statCard} style={{ borderTopColor: color }}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statRate} style={{ color }}>
        {rate}%
      </div>
    </div>
  );
}

export default function CompletionRateStats() {
  const today = new Date();
  const monthAgo = new Date();
  monthAgo.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(monthAgo.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { userName, statusLabel, items }

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await api.get("/completion-stats/", {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExcelDownload = async () => {
    const res = await api.get("/completion-stats/export-excel/", {
      params: { start_date: startDate, end_date: endDate },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `completion_stats_${startDate}_${endDate}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openDetail = async (row, statusFilter, statusLabel) => {
    const res = await api.get("/completion-stats/details/", {
      params: {
        start_date: startDate,
        end_date: endDate,
        user_id: row.user_id,
        ...(statusFilter ? { status: statusFilter } : {}),
      },
    });
    setDetail({ userName: row.user_name, statusLabel, items: res.data });
  };

  if (loading || !data) return <div className={styles.loading}>불러오는 중...</div>;
  const { overall, by_user } = data;




  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>기간별 업무 완료율 통계</h2>
        <div className={styles.filters}>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <span>~</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <button onClick={fetchStats} className={styles.searchBtn}>
            조회
          </button>
          <button onClick={handleExcelDownload} className={styles.excelBtn}>
            엑셀 다운로드
          </button>
        </div>
      </div>

      <div className={styles.summaryRow}>
        <StatCard label="등록 업무 수" value={overall.total} rate={100} color="#4472C4" />
        <StatCard
          label="완료 업무 수"
          value={overall.completed}
          rate={overall.completed_rate}
          color={COLORS.completed}
        />
        <StatCard
          label="진행중 업무 수"
          value={overall.in_progress}
          rate={overall.in_progress_rate}
          color={COLORS.in_progress}
        />
        <StatCard
          label="미진행 업무 수"
          value={overall.not_started}
          rate={overall.not_started_rate}
          color={COLORS.not_started}
        />
      </div>

      <div className={styles.chartsRow}>
        <div className={styles.chartBox}>
          <h4>직원별 상태 비율</h4>
          <div className={styles.pieGrid}>
            {by_user.map((row) => {
              const userPie = [
                { name: "완료", value: row.completed, color: COLORS.completed },
                { name: "진행중", value: row.in_progress, color: COLORS.in_progress },
                { name: "미진행", value: row.not_started, color: COLORS.not_started },
              ].filter((d) => d.value > 0);
              return (
                <div key={row.user_id} className={styles.pieItem}>
                  <ResponsiveContainer width="100%" height={150}>
                    <PieChart>
                      <Pie
                        data={userPie}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={35}
                        outerRadius={55}
                        paddingAngle={3}
                      >
                        {userPie.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className={styles.pieName}>
                    {row.user_name}
                    <span className={styles.pieRate}> 완료 {row.completed_rate}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>



        <div className={styles.chartBox}>
          <h4>직원별 완료 현황</h4>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={by_user}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="user_name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="completed" name="완료" stackId="a" fill={COLORS.completed} />
              <Bar dataKey="in_progress" name="진행중" stackId="a" fill={COLORS.in_progress} />
              <Bar dataKey="not_started" name="미진행" stackId="a" fill={COLORS.not_started} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>직원명</th>
            <th>등록업무수</th>
            <th>완료</th>
            <th>완료율</th>
            <th>진행중</th>
            <th>진행중율</th>
            <th>미진행</th>
            <th>미진행율</th>
          </tr>
        </thead>
        <tbody>
          {by_user.map((row) => (
            <tr key={row.user_id}>
              <td>{row.user_name}</td>
              <td className={styles.clickable} onClick={() => openDetail(row, null, "전체")}>
                {row.total}
              </td>
              <td className={styles.clickable} onClick={() => openDetail(row, "완료", "완료")}>
                {row.completed}
              </td>
              <td className={styles.done}>{row.completed_rate}%</td>
              <td className={styles.clickable} onClick={() => openDetail(row, "진행중", "진행중")}>
                {row.in_progress}
              </td>
              <td className={styles.progress}>{row.in_progress_rate}%</td>
              <td className={styles.clickable} onClick={() => openDetail(row, "미진행", "미진행")}>
                {row.not_started}
              </td>
              <td className={styles.todo}>{row.not_started_rate}%</td>
            </tr>
          ))}
        </tbody>

      </table>

      {detail && (
        <div className={styles.modalOverlay} onClick={() => setDetail(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h4>
                {detail.userName} — {detail.statusLabel} 업무 ({detail.items.length}건)
              </h4>
              <button className={styles.modalClose} onClick={() => setDetail(null)}>
                ✕
              </button>
            </div>
            {detail.items.length === 0 ? (
              <p className={styles.modalEmpty}>해당하는 업무가 없습니다.</p>
            ) : (
              <table className={styles.modalTable}>
                <thead>
                  <tr>
                    <th>등록일</th>
                    <th>업무명</th>
                    <th>업무내용</th>
                    <th>상태</th>
                    <th>이월</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items.map((it) => (
                    <tr key={it.id}>
                      <td>{it.registered_date}</td>
                      <td>{it.task_name}</td>
                      <td className={styles.modalDetail}>{it.task_detail}</td>
                      <td
                        className={
                          it.status === "완료"
                            ? styles.done
                            : it.status === "진행중"
                            ? styles.progress
                            : styles.todo
                        }
                      >
                        {it.status}
                      </td>
                      <td>{it.carried_days > 0 ? `${it.carried_days}일` : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

